export type GroupType = 'AB' | 'LW' | 'FU' | 'SW' | 'LI' | 'PR' | 'SA'

export type DriveType = 'team' | 'personal'

/**
 * Origin of the item: `global` = default/mandatory (read-only in the UI),
 * `custom` = created by the user (editable). Set by `normalizeStructureData()`
 * on load; legacy data is classified heuristically.
 */
export type ItemSource = 'global' | 'custom'

/**
 * Structural role of an OU — replaces the scattered name/parent logic
 * from `isProtectedOu()` with an explicit field.
 */
export type OuKind =
  | 'customer-root'   // Customer root OU (=domainPrefix)
  | 'mandatory'       // Mandatory child under customer root (AB/LW/SW/SA/FU)
  | 'systemhaus'      // _IMP OU at top level
  | 'regular'         // freely created OU

export interface Ou {
  id: string
  name: string
  description?: string
  parent: string | null
  source?: ItemSource
  kind?: OuKind
}

/**
 * A TANSS employee assigned as responsible to a Share/Drive/Folder.
 * `name` is a snapshot at the time of selection — if the name in TANSS
 * changes later, the old display remains until the entry is reopened/saved.
 * `employeeId = -1` marks a migrated free-text entry that must be manually
 * resolved to a TANSS employee (see `migrateResponsiblesV1`).
 */
export interface Responsible {
  employeeId: number
  name: string
}

/** Sentinel for migration entries without a TANSS employee ID. */
export const UNRESOLVED_EMPLOYEE_ID = -1

export interface Share {
  id: string
  name: string           // Share name WITHOUT $ suffix, e.g. "GEW"
  isHidden: boolean      // true → $ suffix (hidden administrative share)
  uncPath: string        // \\Server\ShareName[$] – can be overridden manually
  localPath?: string     // Local path on server, e.g. D:\GEW
  description?: string
  allowedGroupIds?: string[]
  /** Up to 3 responsible employees, order = ranking (position 1 = primary). */
  responsibleEmployees?: Responsible[]
}

export interface Server {
  id: string
  name: string
  hostname: string
  dataRootDrive?: string  // Data drive, e.g. D (without colon)
  description?: string
  shares: Share[]
}

// Drive mapping: a letter (e.g. "P") is mapped via GPO to a
// (sub-)path within a share. A share can have many drives.
export interface Drive {
  id: string
  shareId: string           // Reference to Share.id
  letter: string            // 1 letter, e.g. "P", "M", "Q"
  label: string             // Display/volume name, e.g. "ZentraleBereiche"
  subPath?: string          // Path below the share, e.g. "ZentraleBereiche"
                            // empty = share root, "Users\%USERNAME%" for personal
  type: DriveType           // 'team' = LW filter group, 'personal' = FU filter
  description?: string
  /** Up to 3 responsible employees, order = ranking (position 1 = primary). */
  responsibleEmployees?: Responsible[]
  /** Free-text name of the primary responsible. */
  responsibleName?: string
  /** Free-text name of the deputy. */
  deputyName?: string
}

export interface Folder {
  id: string
  shareId: string
  driveId?: string           // Optional: which drive this folder belongs to
  name: string
  relativePath: string
  parent: string | null
  /**
   * Up to 3 responsible employees, order = ranking (position 1 = primary).
   * Empty or undefined → subfolders inherit from the nearest parent folder or
   * share root (see `getInheritedResponsibles`).
   */
  responsibleEmployees?: Responsible[]
  /** Free-text name of the primary responsible. */
  responsibleName?: string
  /** Free-text name of the deputy. */
  deputyName?: string
}

export interface Group {
  id: string
  type: GroupType
  name: string               // normalized AD group name, e.g. "Fuehrungsebene"
  teamName?: string          // original team name (Excel header), e.g. "Fuehrungsebene"
  description?: string
  scope?: 'global' | 'domainLocal' | 'universal'
  ouId?: string              // OU where the group lives (AD explorer assignment)
  source?: ItemSource
  /**
   * AD nesting: IDs of groups that THIS group is a member of.
   * Typical case: an AB group is a member of multiple LI/FU/SW groups —
   * all users of the AB group thereby inherit the LI/FU/SW membership.
   * User membership is transitive: `u ∈ A` and `A.memberOfGroupIds ∋ B` ⇒ `u ∈ B`.
   */
  memberOfGroupIds?: string[]
}

export interface User {
  id: string
  username: string
  description?: string
  groupIds: string[]
  ouId?: string              // OU where the user lives (AD explorer assignment)
  source?: ItemSource
}

/**
 * `Revoke` is NOT a real NTFS right, but a marker indicating that the
 * inheritance ACE for exactly this group at this folder is actively removed
 * — the group does not appear in the ACL even though parents would have
 * granted it a right. Unlike `None` (DENY), no explicit denial ACE is created
 * that would override rights from memberships in other groups.
 */
export type Rights = 'FullControl' | 'Modify' | 'ReadExecute' | 'Read' | 'Write' | 'ListFolder' | 'None' | 'Revoke'

/**
 * A permission entry references either a concrete folder
 * (`folderId`) or a share root (`shareId`). Exactly ONE of the two
 * fields must be set. Share-root entries stand at the beginning of
 * the inheritance chain — top-level folders inherit from the share root.
 */
export interface Permission {
  folderId?: string
  shareId?: string
  groupId: string
  rights: Rights
  inherited?: boolean
  noInherit?: boolean
}

/** Customer-specific values for standard GPOs. Key = GPO key from backend. */
export type GpoParams = Record<string, Record<string, number | boolean>>

export interface StructureData {
  domainPrefix?: string
  domain?: string
  servers: Server[]
  drives: Drive[]
  ous: Ou[]
  folders: Folder[]
  groups: Group[]
  users: User[]
  permissions: Permission[]
  /** Per standard GPO the customer-specific parameter values (optional). */
  gpoParams?: GpoParams
  /** Direct department→group links, independent of user memberships. */
  departmentGroupLinks?: { abGroupId: string; groupId: string }[]
  /** Default storage locations per group type / user in AD explorer (OU IDs). */
  defaultParents?: import('./explorer/types.ts').DefaultParents
}

export interface CustomerStructure {
  id: string
  tenant_id: string
  customer_id: string
  version: number
  structure_data: StructureData
  notes?: string
  created_at: string
  created_by_user_id?: string
}

export const GROUP_TYPE_LABELS: Record<GroupType, string> = {
  AB: 'Abteilungen',
  LW: 'Laufwerke',
  FU: 'Funktionen',
  SW: 'Softwarezugriffe',
  LI: 'Lizenzen',
  PR: 'Printer',
  SA: 'SysAdmin',
}

export const GROUP_TYPES: GroupType[] = ['AB', 'LW', 'FU', 'SW', 'LI', 'PR', 'SA']

export const DRIVE_TYPE_LABELS: Record<DriveType, string> = {
  team: 'Team / Abteilung',
  personal: 'Persönlich (pro User)',
}

export const RIGHTS_OPTIONS: Array<{ value: Rights; label: string; short: string }> = [
  { value: 'FullControl', label: 'Vollzugriff', short: 'FC' },
  { value: 'Modify', label: 'Ändern', short: 'M' },
  { value: 'ReadExecute', label: 'Lesen & Ausführen', short: 'RX' },
  { value: 'Read', label: 'Lesen', short: 'R' },
  { value: 'Write', label: 'Schreiben', short: 'W' },
  { value: 'ListFolder', label: 'Inhalt auflisten', short: 'L' },
  { value: 'None', label: 'Kein Zugriff (DENY)', short: 'DENY' },
  { value: 'Revoke', label: 'Entziehen (ohne DENY)', short: '-' },
]

/**
 * Curated customer presets. Represent the levels relevant for everyday use
 * and hide confusing NTFS granularity (ListFolder, pure Read,
 * create-files vs. create-folders). Each preset maps to one of the
 * existing `Rights` values — the NTFS value is still persisted,
 * only the UI is simplified.
 *
 * Those needing full NTFS control use the "Extended" toggle in the
 * matrix — which then shows `RIGHTS_OPTIONS` again.
 */
export type PermissionPreset = 'inherit' | 'revoke' | 'deny' | 'read' | 'dropbox' | 'modify' | 'full'

export interface PresetDef {
  value: PermissionPreset
  label: string
  short: string
  /** null = no entry, inherits from parent */
  rights: Rights | null
  description: string
  category: 'green' | 'orange' | 'yellow' | 'red' | 'white' | 'mixed' | 'gray'
}

export const PERMISSION_PRESETS: PresetDef[] = [
  {
    value: 'inherit',
    label: '(vererben)',
    short: '—',
    rights: null,
    category: 'white',
    description: 'Erbt vom Parent-Ordner',
  },
  {
    value: 'revoke',
    label: 'Entziehen (ohne DENY)',
    short: '-',
    rights: 'Revoke',
    category: 'gray',
    description:
      'Gruppe wird in der ACL für diesen Ordner nicht aufgeführt — kein Zugriff, ' +
      'aber kein DENY-ACE. Mitgliedschaften in anderen Gruppen behalten ihre Rechte.',
  },
  {
    value: 'deny',
    label: 'DENY – Kein Zugriff',
    short: 'DENY',
    rights: 'None',
    category: 'red',
    description: 'Explizit gesperrt — überschreibt auch Rechte aus anderen Gruppen!',
  },
  {
    value: 'read',
    label: 'Lesen',
    short: 'R',
    rights: 'ReadExecute',
    category: 'yellow',
    description: 'Lesen, Ausführen, Ordner durchsuchen',
  },
  {
    value: 'dropbox',
    label: 'Einwurf-Postfach',
    short: 'EIN',
    rights: 'Write',
    category: 'mixed',
    description: 'Nur reinwerfen, kein Lesen, kein Löschen (Drop-Off / Scan)',
  },
  {
    value: 'modify',
    label: 'Bearbeiten',
    short: 'M',
    rights: 'Modify',
    category: 'green',
    description: 'Lesen + Schreiben + Löschen (Standard für Mitarbeiter)',
  },
  {
    value: 'full',
    label: 'Vollzugriff',
    short: 'FC',
    rights: 'FullControl',
    category: 'orange',
    description: 'Plus Rechte ändern und Besitz übernehmen — nur für Admins',
  },
]

/**
 * Maps a stored NTFS value back to the preset to display.
 * `Read` and `ListFolder` are both shown as `read` (ReadExecute preset)
 * so the picker display stays consistent. Those who really need the
 * distinction must enable extended mode.
 */
export function rightsToPreset(r: Rights | null): PermissionPreset {
  if (r === null) return 'inherit'
  if (r === 'Revoke') return 'revoke'
  if (r === 'None') return 'deny'
  if (r === 'FullControl') return 'full'
  if (r === 'Modify') return 'modify'
  if (r === 'Write') return 'dropbox'
  if (r === 'ReadExecute' || r === 'Read' || r === 'ListFolder') return 'read'
  return 'inherit'
}

export function presetByValue(value: PermissionPreset): PresetDef {
  return PERMISSION_PRESETS.find((p) => p.value === value) ?? PERMISSION_PRESETS[0]
}

export function fullGroupName(prefix: string | undefined, type: GroupType, name: string): string {
  return `${prefix || 'PREFIX'}_${type}_${name}`
}

export function groupOuPath(prefix: string | undefined, type: GroupType, domain: string | undefined): string {
  const dcParts = (domain ?? 'domain.local').split('.').map((p) => `DC=${p}`).join(',')
  return `OU=${type},OU=Groups,OU=${prefix || 'PREFIX'},${dcParts}`
}

/**
 * Builds the UNC path from host + share name + hidden flag.
 * Hostname preferred, falls back to server name.
 */
export function deriveUncPath(hostname: string, shareName: string, isHidden: boolean): string {
  const host = hostname.trim()
  const raw = shareName.trim().replace(/\$+$/, '')
  if (!host || !raw) return ''
  return `\\\\${host}\\${raw}${isHidden ? '$' : ''}`
}

/**
 * Transitive closure of a group's memberships. Returns the given
 * group ID plus all groups it is directly or indirectly a member of
 * (via `memberOfGroupIds`). Cycle-safe.
 */
export function expandGroupMembership(
  startGroupId: string,
  groups: ReadonlyArray<Group>,
): Set<string> {
  const byId = new Map(groups.map((g) => [g.id, g]))
  const out = new Set<string>()
  const stack = [startGroupId]
  while (stack.length > 0) {
    const id = stack.pop() as string
    if (out.has(id)) continue
    out.add(id)
    const g = byId.get(id)
    if (!g?.memberOfGroupIds) continue
    for (const parentId of g.memberOfGroupIds) stack.push(parentId)
  }
  return out
}

/**
 * Effective group memberships of a user = direct memberships
 * plus all transitively inherited ones via AD nesting (`Group.memberOfGroupIds`).
 */
export function effectiveGroupIdsForUser(
  user: User,
  groups: ReadonlyArray<Group>,
): Set<string> {
  const out = new Set<string>()
  for (const gid of user.groupIds) {
    for (const id of expandGroupMembership(gid, groups)) out.add(id)
  }
  return out
}

/**
 * For each inherited group membership, the list of direct groups
 * through which it is inherited. Key = inherited group ID, Value = set of direct
 * group IDs (usually AB) that carry the path there. Only **indirect**
 * memberships are in the map — direct ones (i.e. `user.groupIds`) are NOT.
 */
export function inheritedGroupSources(
  user: User,
  groups: ReadonlyArray<Group>,
): Map<string, Set<string>> {
  const direct = new Set(user.groupIds)
  const map = new Map<string, Set<string>>()
  for (const gid of user.groupIds) {
    const expanded = expandGroupMembership(gid, groups)
    for (const inheritedId of expanded) {
      if (inheritedId === gid) continue       // don't mark itself as inherited
      if (direct.has(inheritedId)) continue   // also direct → direct wins
      if (!map.has(inheritedId)) map.set(inheritedId, new Set())
      map.get(inheritedId)!.add(gid)
    }
  }
  return map
}

/**
 * Marker field at the `Group` level is interpreted as "already has nesting".
 * New: structure-wide identifier `__nestingMigrated` in `StructureData` (optional,
 * ignored by the backend DB since `structure_data` is stored as free JSON).
 */
const NESTING_MIGRATED_FLAG = '__nestingMigratedV1' as const

type MigrationFlag = { [NESTING_MIGRATED_FLAG]?: true }

/**
 * One-time migration in two steps:
 *
 * 1. **Legacy `departmentGroupLinks`** (direct AB→group links from an earlier
 *    implementation) are transferred into `memberOfGroupIds` of the respective AB group.
 *    The old field is removed afterwards.
 * 2. **Per-user memberships**: For each (AB group, non-AB group) combination
 *    where ALL users of the AB group are directly in the non-AB group
 *    AND the AB has at least one user, the AB is added as a member of the
 *    non-AB group and direct user memberships for that non-AB group are removed.
 *    Partially covered combinations remain unchanged.
 *
 * Idempotent via the flag `__nestingMigratedV1` — runs at most once per structure.
 */
export function migrateStructureToNestedGroups(data: StructureData): StructureData {
  if ((data as StructureData & MigrationFlag)[NESTING_MIGRATED_FLAG]) return data

  const abGroups = data.groups.filter((g) => g.type === 'AB')
  const nonAbGroups = data.groups.filter((g) => g.type !== 'AB')

  const usersByAb = new Map<string, string[]>()
  for (const ab of abGroups) {
    usersByAb.set(ab.id, data.users.filter((u) => u.groupIds.includes(ab.id)).map((u) => u.id))
  }

  const newNestings = new Map<string, Set<string>>()          // abId -> Set<nonAbId>
  const removeDirectMembership = new Map<string, Set<string>>() // userId -> Set<nonAbId>

  // Step 1: transfer legacy links
  for (const link of data.departmentGroupLinks ?? []) {
    if (!newNestings.has(link.abGroupId)) newNestings.set(link.abGroupId, new Set())
    newNestings.get(link.abGroupId)!.add(link.groupId)
  }

  // Step 2: convert fully covered user memberships
  for (const ab of abGroups) {
    const abUserIds = usersByAb.get(ab.id) ?? []
    if (abUserIds.length === 0) continue
    for (const target of nonAbGroups) {
      const allIn = abUserIds.every((uid) => {
        const u = data.users.find((x) => x.id === uid)
        return u?.groupIds.includes(target.id)
      })
      if (!allIn) continue
      if (!newNestings.has(ab.id)) newNestings.set(ab.id, new Set())
      newNestings.get(ab.id)!.add(target.id)
      for (const uid of abUserIds) {
        if (!removeDirectMembership.has(uid)) removeDirectMembership.set(uid, new Set())
        removeDirectMembership.get(uid)!.add(target.id)
      }
    }
  }

  const nextGroups = data.groups.map((g) => {
    const added = newNestings.get(g.id)
    if (!added || added.size === 0) return g
    const existing = new Set(g.memberOfGroupIds ?? [])
    for (const id of added) existing.add(id)
    return { ...g, memberOfGroupIds: [...existing] }
  })

  const nextUsers = data.users.map((u) => {
    const remove = removeDirectMembership.get(u.id)
    if (!remove || remove.size === 0) return u
    return { ...u, groupIds: u.groupIds.filter((gid) => !remove.has(gid)) }
  })

  const next: StructureData & MigrationFlag = {
    ...data,
    groups: nextGroups,
    users: nextUsers,
    [NESTING_MIGRATED_FLAG]: true,
  }
  // Remove legacy field after it has been transferred into memberOfGroupIds
  delete next.departmentGroupLinks
  return next
}

// ─── Responsibles: migration from free text to TANSS employee list ──────────

const RESPONSIBLES_MIGRATED_FLAG = '__responsiblesMigratedV1' as const

type ResponsiblesMigrationFlag = { [RESPONSIBLES_MIGRATED_FLAG]?: true }

/**
 * Old form of the responsible fields on Share/Drive/Folder, as stored before
 * the introduction of `responsibleEmployees: Responsible[]`.
 * No longer part of the `Share`/`Drive`/`Folder` interfaces, but when loading
 * old JSON structures the fields may still appear.
 */
interface LegacyResponsibleFields {
  responsibleName?: string
  responsibleEmployeeId?: string | number
  deputyName?: string
  deputyEmployeeId?: string | number
}

function legacyToResponsibles(
  legacy: LegacyResponsibleFields,
  existing: Responsible[] | undefined,
): Responsible[] {
  // If new data already exists (e.g. partially migrated): do not overwrite.
  if (existing && existing.length > 0) return existing
  const out: Responsible[] = []
  if (legacy.responsibleName || legacy.responsibleEmployeeId != null) {
    const rawId = legacy.responsibleEmployeeId
    const id = typeof rawId === 'number' ? rawId : rawId ? Number.parseInt(rawId, 10) : NaN
    out.push({
      employeeId: Number.isFinite(id) ? id : UNRESOLVED_EMPLOYEE_ID,
      name: legacy.responsibleName?.trim() || '—',
    })
  }
  if (legacy.deputyName || legacy.deputyEmployeeId != null) {
    const rawId = legacy.deputyEmployeeId
    const id = typeof rawId === 'number' ? rawId : rawId ? Number.parseInt(rawId, 10) : NaN
    out.push({
      employeeId: Number.isFinite(id) ? id : UNRESOLVED_EMPLOYEE_ID,
      name: legacy.deputyName?.trim() || '—',
    })
  }
  return out
}

type MaybeLegacy<T> = T & LegacyResponsibleFields

function stripLegacyFields<T extends object>(item: MaybeLegacy<T>): T {
  const copy = { ...item } as MaybeLegacy<T>
  delete copy.responsibleName
  delete copy.responsibleEmployeeId
  delete copy.deputyName
  delete copy.deputyEmployeeId
  return copy as T
}

/**
 * One-time migration of old free-text responsible fields
 * (`responsibleName`/`deputyName` + employee IDs) to the new
 * `responsibleEmployees` list. Idempotent via `__responsiblesMigratedV1`.
 * Entries without a mapped employee ID get `UNRESOLVED_EMPLOYEE_ID` (-1)
 * and must be manually resolved to a TANSS employee by the user via the picker.
 */
export function migrateResponsiblesV1(data: StructureData): StructureData {
  if ((data as StructureData & ResponsiblesMigrationFlag)[RESPONSIBLES_MIGRATED_FLAG]) {
    return data
  }

  const nextServers: Server[] = data.servers.map((s) => ({
    ...s,
    shares: s.shares.map((sh) => {
      const legacy = sh as MaybeLegacy<Share>
      const responsibleEmployees = legacyToResponsibles(legacy, legacy.responsibleEmployees)
      const cleaned = stripLegacyFields<Share>(legacy)
      return {
        ...cleaned,
        responsibleEmployees: responsibleEmployees.length > 0 ? responsibleEmployees : cleaned.responsibleEmployees,
      }
    }),
  }))

  const nextDrives: Drive[] = data.drives.map((d) => {
    const legacy = d as MaybeLegacy<Drive>
    const responsibleEmployees = legacyToResponsibles(legacy, legacy.responsibleEmployees)
    const cleaned = stripLegacyFields<Drive>(legacy)
    return {
      ...cleaned,
      responsibleEmployees: responsibleEmployees.length > 0 ? responsibleEmployees : cleaned.responsibleEmployees,
    }
  })

  const nextFolders: Folder[] = data.folders.map((f) => {
    const legacy = f as MaybeLegacy<Folder>
    const responsibleEmployees = legacyToResponsibles(legacy, legacy.responsibleEmployees)
    const cleaned = stripLegacyFields<Folder>(legacy)
    return {
      ...cleaned,
      responsibleEmployees: responsibleEmployees.length > 0 ? responsibleEmployees : cleaned.responsibleEmployees,
    }
  })

  return {
    ...data,
    servers: nextServers,
    drives: nextDrives,
    folders: nextFolders,
    [RESPONSIBLES_MIGRATED_FLAG]: true,
  } as StructureData & ResponsiblesMigrationFlag
}

/**
 * Display string of a responsible list: names separated by commas.
 * Used for Excel export and tooltip texts among others. Empty list → "".
 */
export function formatResponsiblesDisplay(list: Responsible[] | undefined): string {
  if (!list || list.length === 0) return ''
  return list.map((r) => r.name).filter(Boolean).join(', ')
}
